import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import { BadgeCheck, LayoutDashboard, ShieldCheck, TriangleAlert, Workflow, X } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { PasswordInput } from '@/shared/ui/PasswordInput';
import { Label } from '@/shared/ui/Label';
import { Checkbox } from '@/shared/ui/Checkbox';
import { Alert } from '@/shared/ui/Alert';
import { useAuthStore } from '@/entities/session/model/authStore';
import { runtimeConfig } from '@/app/config/runtimeConfig';
import { splitClientInfo } from '@/entities/session/lib/clientInfoText';
import { LanguageMenu } from '@/features/shell/header/LanguageMenu';
import { ThemeToggle } from '@/features/shell/header/ThemeToggle';
import { ConnectionPicker } from '../connectionPicker/ConnectionPicker';
import { useLogin, type LoginFormValues } from './useLogin';
import { needsAdmin } from './loginError';
import { LoginBackdrop } from './LoginBackdrop';
import { LicenseNoticePanel } from './LicenseNoticePanel';
import { RotatingWord } from './RotatingWord';
import { opensNodeView, rememberViewMode } from '@/features/node/model/viewMode';
import { OctoMark } from '@/shared/ui/OctoMark';

/** Sol paneldeki üç satır — markanın ne yaptığını tek bakışta anlatır. */
const PITCH = [
  { key: 'screens', Icon: LayoutDashboard },
  { key: 'flows', Icon: Workflow },
  { key: 'secure', Icon: ShieldCheck },
] as const;

export function LoginPage() {
  const { t } = useTranslation();
  const status = useAuthStore((s) => s.status);
  const lastReason = useAuthStore((s) => s.lastLogoutReason);
  const lastDevice = useAuthStore((s) => s.lastLogoutDevice);
  const location = useLocation();
  const {
    submit,
    candidates,
    selected,
    setSelected,
    error,
    loading,
    notice,
    acknowledgeNotice,
    resetCandidates,
  } = useLogin();
  const form = useForm<LoginFormValues>({ defaultValues: { userCode: '', password: '', remember: true } });
  const remember = useWatch({ control: form.control, name: 'remember' });

  /*
   * Yönlendirme, giriş akışı BİTENE kadar bekletilir.
   *
   * `login()` çözülür çözülmez durum "authenticated" oluyor ve sayfa yeniden çiziliyor;
   * lisans kapısı ise hemen ardından gelen bir istekle kuruluyordu. Aradaki o tek karede
   * yönlendirme çalışıyor ve uyarı hiç görünmüyordu — `loading` bayrağı akış bitene dek
   * (kapı dahil) açık kalıyor, bu yüzden kapıyı o tutuyor.
   */
  if (status === 'authenticated' && !notice && !loading) {
    const from = (location.state as { from?: string } | null)?.from ?? null;
    // Derin linkle gelindiyse oraya; yoksa görünüm tercihine bakılır: yalnızca node
    // lisanslı kullanıcının masaüstünde işi yok, "girişte node görünümü" diyen de el
    // terminali akışını bekliyor (web: LoginResponse.OpensNodeView).
    const to = from ?? (opensNodeView(useAuthStore.getState().user) ? '/node' : '/');
    if (to === '/node') rememberViewMode('node');
    return <Navigate to={to} replace />;
  }

  const errorText = error
    ? error.kind === 'unknown'
      ? error.raw || t('auth.error.unknown')
      : t(`auth.error.${error.kind}`)
    : null;

  return (
    <div className="bg-bg relative flex min-h-dvh flex-col overflow-hidden">
      <LoginBackdrop />

      <div className="relative z-10 flex justify-end gap-1 p-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <LanguageMenu />
        <ThemeToggle />
      </div>

      <main className="relative z-10 flex flex-1 items-center justify-center px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <div className="grid w-full max-w-5xl items-center gap-12 lg:grid-cols-[1fr_24rem] lg:gap-16">
          {/* Marka paneli yalnız geniş ekranda: dar ekranda kartın kendi başlığı var,
              iki logo alt alta gelirse form katlanın altına düşüyor. */}
          <section className="octo-rise hidden flex-col gap-8 lg:flex">
            <div className="flex items-center gap-4">
              <OctoMark className="octo-float h-16 w-16" title="Octo" />
              <div>
                <p className="text-3xl font-bold tracking-wide">OCTO</p>
                <p className="text-fg-muted text-sm">{t('auth.tagline')}</p>
                <RotatingWord />
              </div>
            </div>
            <ul className="flex flex-col gap-4">
              {PITCH.map(({ key, Icon }, i) => (
                <li
                  key={key}
                  className="octo-rise flex items-start gap-3"
                  style={{ animationDelay: `${120 + i * 90}ms` }}
                >
                  <span className="bg-brand-500/10 text-brand-600 rounded-lg p-2">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{t(`auth.pitch.${key}.title`)}</span>
                    <span className="text-fg-muted block text-sm">{t(`auth.pitch.${key}.body`)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <div
            className="octo-rise border-border bg-bg-elevated/90 mx-auto w-full max-w-sm rounded-2xl border p-5 shadow-[var(--shadow)] backdrop-blur-xl sm:p-6"
            style={{ animationDelay: '60ms' }}
          >
            <div className="mb-5 flex flex-col items-center gap-2 text-center lg:hidden">
              <OctoMark className="h-14 w-14" title="Octo" />
              <span className="text-xl font-bold tracking-wide">OCTO</span>
            </div>
            <h1 className="text-lg font-semibold">{t('auth.title')}</h1>
            <p className="text-fg-muted mt-1 mb-4 text-sm">{t('auth.subtitle')}</p>

            {lastReason === 'expired' || lastReason === 'session-closed' ? (
              <Alert tone="warning" className="mb-3">
                {t('auth.sessionExpired.body')}
                {/* Oturumu düşüren cihaz biliniyorsa söylenir: kullanıcı giriş ekranına
                    düştüğünde uyarı penceresi çoktan kapanmış oluyor. */}
                {lastDevice ? (
                  <span className="mt-1 block text-xs">
                    {t('auth.sessionExpired.fromDevice')}: {splitClientInfo(lastDevice).device}
                  </span>
                ) : null}
              </Alert>
            ) : null}
            {lastReason === 'license' ? (
              <Alert tone="warning" className="mb-3">
                {t('auth.license.revoked')}
              </Alert>
            ) : null}

            {notice ? (
              <LicenseNoticePanel notice={notice} onContinue={acknowledgeNotice} />
            ) : (
              <form
                className="flex flex-col gap-4"
                onSubmit={form.handleSubmit((v) => submit(v, t('auth.invalid')))}
                noValidate
              >
                <div className="octo-rise flex flex-col gap-1" style={{ animationDelay: '120ms' }}>
                  <Label htmlFor="userCode">{t('auth.userCode')}</Label>
                  <Input
                    id="userCode"
                    autoComplete="username"
                    autoFocus
                    // Mobil klavye tuzakları: ilk harfi büyüten otomatik düzeltme
                    // kullanıcı kodunu bozuyor, "git" tuşu da doğrudan göndermemeli.
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    enterKeyHint="next"
                    {...form.register('userCode', { required: true })}
                  />
                </div>
                <div className="octo-rise flex flex-col gap-1" style={{ animationDelay: '180ms' }}>
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <PasswordInput
                    id="password"
                    autoComplete="current-password"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    enterKeyHint="go"
                    {...form.register('password', { required: true })}
                  />
                </div>

                {candidates ? (
                  <ConnectionPicker
                    items={candidates}
                    value={selected?.connectionId ?? null}
                    onChange={(c) => {
                      setSelected(c);
                      resetCandidates();
                    }}
                  />
                ) : null}
                {selected ? (
                  <div className="bg-bg-muted flex items-center justify-between rounded-md px-3 py-2 text-sm">
                    <span className="min-w-0 truncate">
                      {t('auth.connection')}: <b>{selected.customerName || selected.name}</b>
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      aria-label={t('auth.clearConnection')}
                      onClick={() => setSelected(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null}

                <label
                  className="octo-rise flex items-center gap-2 text-sm"
                  style={{ animationDelay: '240ms' }}
                >
                  <Checkbox
                    checked={remember}
                    onCheckedChange={(v) => form.setValue('remember', v === true)}
                  />
                  {t('auth.remember')}
                </label>

                {errorText ? (
                  <Alert tone="danger" className="octo-shake flex items-start gap-2">
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    <span className="min-w-0">
                      {errorText}
                      {needsAdmin(error!.kind) ? (
                        <span className="mt-0.5 block text-xs opacity-80">{t('auth.error.askAdmin')}</span>
                      ) : null}
                    </span>
                  </Alert>
                ) : null}

                <Button
                  type="submit"
                  size="lg"
                  loading={loading}
                  className="octo-rise w-full transition-transform active:scale-[0.99]"
                  style={{ animationDelay: '300ms' }}
                >
                  {loading ? t('auth.loggingIn') : t('auth.login')}
                </Button>
              </form>
            )}

            {/* Sürüm giriş ekranında da yazılı: destek istenen ilk bilgi bu ve kullanıcı
                onu görmek için giriş yapmak zorunda kalıyordu (menüde duruyor). */}
            <p className="text-fg-subtle mt-5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs">
              <span className="inline-flex items-center gap-1.5">
                <BadgeCheck className="h-3.5 w-3.5" />
                {t('auth.poweredBy')}
              </span>
              <span aria-hidden="true">·</span>
              <span className="tabular-nums">
                {t('shell.user.version')} {runtimeConfig().appVersion}
              </span>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
